import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Form, Alert, Modal, Card, Row, Col, Badge, InputGroup } from 'react-bootstrap';
import { questionService, PublishQuestionsRequest } from '../../../services/questionService';
import { entityService, Entity } from '../../../services/entityService';
import { Year, yearService } from '../../../services/yearService';
import { questionBaseService, Question } from '../../../services/questionBaseService';
import { itemBaseService, Item } from '../../../services/itemBaseService';
import { actionBaseService, Action } from '../../../services/actionBaseService';
import { areaService, Area } from '../../../services/areaService';

interface PublishFormData {
    selectedYear: string;
    selectedEntities: string[];
    selectedAreas: string[];
    selectedQuestions: string[];
    selectedItems: string[];
    selectedActions: string[];
}

const PublishAdminPage: React.FC = () => {
    const history = useHistory();
    const [entities, setEntities] = useState<Entity[]>([]);
    const [years, setYears] = useState<Year[]>([]);
    const [areas, setAreas] = useState<Area[]>([]);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [items, setItems] = useState<Item[]>([]);
    const [actions, setActions] = useState<Action[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showPublishModal, setShowPublishModal] = useState<boolean>(false);
    const [isPublishing, setIsPublishing] = useState<boolean>(false);
    const [formData, setFormData] = useState<PublishFormData>({
        selectedYear: '',
        selectedEntities: [],
        selectedAreas: [],
        selectedQuestions: [],
        selectedItems: [],
        selectedActions: []
    });

    // Filter states for each selection type
    const [entityFilter, setEntityFilter] = useState<string>('');
    const [areaFilter, setAreaFilter] = useState<string>('');
    const [questionFilter, setQuestionFilter] = useState<string>('');
    const [itemFilter, setItemFilter] = useState<string>('');
    const [actionFilter, setActionFilter] = useState<string>('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch entities - filter only active entities
                const entitiesData = await entityService.getEntities({ status: 'ATIVO' });
                setEntities(entitiesData.content || entitiesData);

                // Fetch all years for admin purposes
                const yearsData = await yearService.getAllYears();
                setYears(yearsData);

                // Fetch base questions
                const questionsData = await questionBaseService.getQuestions({ page: 0, size: 1000 });
                setQuestions(questionsData.content || []);

                // Fetch base items
                const itemsData = await itemBaseService.getItems({ page: 0, size: 1000 });
                setItems(itemsData.content || []);

                // Fetch base actions
                const actionsData = await actionBaseService.getActions({ page: 0, size: 1000 });
                setActions(actionsData.content || []);

                // Fetch all areas
                const areasData = await areaService.getAreas();
                setAreas(areasData);
            } catch (err) {
                setError('Erro ao carregar dados. Por favor, tente novamente.');
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleBack = () => {
        history.push('/admin');
    };

    const handleYearChange = (yearId: string) => {
        setFormData(prev => ({
            ...prev,
            selectedYear: yearId
        }));
    };

    const handleEntitySelection = (entityId: string, checked: boolean) => {
        setFormData(prev => ({
            ...prev,
            selectedEntities: checked
                ? [...prev.selectedEntities, entityId]
                : prev.selectedEntities.filter(id => id !== entityId)
        }));
    };

    // Filter entities based on filter input and sort alphabetically
    const filteredEntities = React.useMemo(() => {
        let filtered = entities;
        if (entityFilter.trim()) {
            filtered = entities.filter(entity =>
                entity.nome.toLowerCase().includes(entityFilter.toLowerCase()) ||
                entity.status.toLowerCase().includes(entityFilter.toLowerCase())
            );
        }
        return filtered.sort((a, b) => a.nome.localeCompare(b.nome));
    }, [entities, entityFilter]);

    // Filter areas based on filter input and sort alphabetically
    const filteredAreas = React.useMemo(() => {
        let filtered = areas;
        if (areaFilter.trim()) {
            filtered = areas.filter(area =>
                area.nome.toLowerCase().includes(areaFilter.toLowerCase()) ||
                area.status.toLowerCase().includes(areaFilter.toLowerCase())
            );
        }
        return filtered.sort((a, b) => a.nome.localeCompare(b.nome));
    }, [areas, areaFilter]);

    // Filter questions based on selected areas and filter input, then sort alphabetically
    const filteredQuestions = React.useMemo(() => {
        let areaFilteredQuestions = questions;
        if (formData.selectedAreas.length > 0) {
            areaFilteredQuestions = questions.filter(question =>
                formData.selectedAreas.includes(question.areaId)
            );
        }

        let filtered = areaFilteredQuestions;
        if (questionFilter.trim()) {
            filtered = areaFilteredQuestions.filter(question =>
                question.titulo.toLowerCase().includes(questionFilter.toLowerCase()) ||
                question.tipoBase.toLowerCase().includes(questionFilter.toLowerCase())
            );
        }
        return filtered.sort((a, b) => a.titulo.localeCompare(b.titulo));
    }, [questions, formData.selectedAreas, questionFilter]);

    // Filter items based on selected questions and filter input, then sort alphabetically
    const filteredItems = React.useMemo(() => {
        let questionFilteredItems = items;
        if (formData.selectedQuestions.length > 0) {
            questionFilteredItems = items.filter(item =>
                formData.selectedQuestions.includes(item.questaoBaseId || '')
            );
        }

        let filtered = questionFilteredItems;
        if (itemFilter.trim()) {
            filtered = questionFilteredItems.filter(item =>
                item.titulo.toLowerCase().includes(itemFilter.toLowerCase()) ||
                item.tipoBase.toLowerCase().includes(itemFilter.toLowerCase())
            );
        }
        return filtered.sort((a, b) => a.titulo.localeCompare(b.titulo));
    }, [items, formData.selectedQuestions, itemFilter]);

    // Filter actions based on selected items and filter input, then sort alphabetically
    const filteredActions = React.useMemo(() => {
        let itemFilteredActions = actions;
        if (formData.selectedItems.length > 0) {
            itemFilteredActions = actions.filter(action =>
                formData.selectedItems.includes(action.itemBaseId || '')
            );
        }

        let filtered = itemFilteredActions;
        if (actionFilter.trim()) {
            filtered = itemFilteredActions.filter(action =>
                action.titulo.toLowerCase().includes(actionFilter.toLowerCase()) ||
                action.tipoBase.toLowerCase().includes(actionFilter.toLowerCase())
            );
        }
        return filtered.sort((a, b) => a.titulo.localeCompare(b.titulo));
    }, [actions, formData.selectedItems, actionFilter]);

    // Reset dependent selections when parent selection changes
    const handleAreaSelection = (areaId: string, checked: boolean) => {
        setFormData(prev => ({
            ...prev,
            selectedAreas: checked
                ? [...prev.selectedAreas, areaId]
                : prev.selectedAreas.filter(id => id !== areaId),
            // Reset dependent selections
            selectedQuestions: [],
            selectedItems: [],
            selectedActions: [],
        }));
    };

    const handleQuestionSelection = (questionId: string, checked: boolean) => {
        setFormData(prev => ({
            ...prev,
            selectedQuestions: checked
                ? [...prev.selectedQuestions, questionId]
                : prev.selectedQuestions.filter(id => id !== questionId),
            // Reset dependent selections
            selectedItems: [],
            selectedActions: [],
        }));
    };

    const handleItemSelection = (itemId: string, checked: boolean) => {
        setFormData(prev => ({
            ...prev,
            selectedItems: checked
                ? [...prev.selectedItems, itemId]
                : prev.selectedItems.filter(id => id !== itemId),
            // Reset dependent selections
            selectedActions: [],
        }));
    };

    const handleActionSelection = (actionId: string, checked: boolean) => {
        setFormData(prev => ({
            ...prev,
            selectedActions: checked
                ? [...prev.selectedActions, actionId]
                : prev.selectedActions.filter(id => id !== actionId)
        }));
    };

    const handleSelectAllEntities = () => {
        setFormData(prev => ({
            ...prev,
            selectedEntities: filteredEntities.map(e => e.entidade_id),
        }));
    };

    const handleClearAllEntities = () => {
        setFormData(prev => ({
            ...prev,
            selectedEntities: [],
        }));
    };

    const handleSelectAllAreas = () => {
        setFormData(prev => ({
            ...prev,
            selectedAreas: filteredAreas.map(a => a.area_id || ''),
        }));
    };

    const handleClearAllAreas = () => {
        setFormData(prev => ({
            ...prev,
            selectedAreas: [],
        }));
    };

    const handleSelectAllQuestions = () => {
        setFormData(prev => ({
            ...prev,
            selectedQuestions: filteredQuestions.map(q => q.questaoBaseId || ''),
        }));
    };

    const handleClearAllQuestions = () => {
        setFormData(prev => ({
            ...prev,
            selectedQuestions: [],
        }));
    };

    const handleSelectAllItems = () => {
        setFormData(prev => ({
            ...prev,
            selectedItems: filteredItems.map(i => i.itemBaseId || ''),
        }));
    };

    const handleClearAllItems = () => {
        setFormData(prev => ({
            ...prev,
            selectedItems: [],
        }));
    };

    const handleSelectAllActions = () => {
        setFormData(prev => ({
            ...prev,
            selectedActions: filteredActions.map(a => a.acaoBaseId || ''),
        }));
    };

    const handleClearAllActions = () => {
        setFormData(prev => ({
            ...prev,
            selectedActions: [],
        }));
    };

    const handlePublishClick = () => {
        if (!formData.selectedYear) {
            setError('Por favor, selecione um ano.');
            return;
        }

        if (formData.selectedEntities.length === 0) {
            setError('Por favor, selecione pelo menos uma entidade.');
            return;
        }

        if (formData.selectedAreas.length === 0) {
            setError('Por favor, selecione pelo menos uma área.');
            return;
        }

        if (formData.selectedQuestions.length === 0) {
            setError('Por favor, selecione pelo menos uma questão.');
            return;
        }

        if (formData.selectedItems.length === 0) {
            setError('Por favor, selecione pelo menos um item.');
            return;
        }

        if (formData.selectedActions.length === 0) {
            setError('Por favor, selecione pelo menos uma ação.');
            return;
        }

        setShowPublishModal(true);
    };

    const handlePublishModalClose = () => {
        setShowPublishModal(false);
        setIsPublishing(false);
    };

    const handleSubmitPublish = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            setIsPublishing(true);
            setError(null);

            const publishRequest: PublishQuestionsRequest = {
                yearId: parseInt(formData.selectedYear),
                entityIds: formData.selectedEntities,
                questionIds: formData.selectedQuestions,
                itemIds: formData.selectedItems,
                actionIds: formData.selectedActions,
                areaIds: formData.selectedAreas
            };

            await questionService.publishQuestionsForYear(publishRequest);

            setSuccessMessage('Questões, itens e ações publicados com sucesso!');
            handlePublishModalClose();

            // Reset form after successful publish
            setFormData({
                selectedYear: '',
                selectedEntities: [],
                selectedAreas: [],
                selectedQuestions: [],
                selectedItems: [],
                selectedActions: []
            });
        } catch (err) {
            setError('Erro ao publicar questões, itens e ações. Por favor, tente novamente.');
            console.error('Error publishing questions, items and actions:', err);
        } finally {
            setIsPublishing(false);
        }
    };

    const getSelectedYearName = () => {
        const year = years.find(y => y.ano_fiscal_id.toString() === formData.selectedYear);
        return year ? year.ano.toString() : '';
    };


    if (loading) {
        return <div className="text-center p-5">Carregando...</div>;
    }

    return (
        <div className="container-fluid p-4">
            <Button
                variant="outline-secondary"
                onClick={handleBack}
                className="mb-3 d-flex align-items-center gap-2"
            >
                <span>←</span> Voltar
            </Button>

            <div className="dashboard-header mb-4">
                <h2 className="mb-0">Publicar Questões, Itens e Ações</h2>
                <p className="text-muted mb-0">
                    Selecione o ano, entidades, áreas, questões, itens e ações para publicar.
                    As seleções são hierárquicas: áreas → questões → itens → ações.
                </p>
            </div>

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            {successMessage && (
                <Alert variant="success" onClose={() => setSuccessMessage(null)} dismissible>
                    {successMessage}
                </Alert>
            )}

            {/* Year Selection */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Selecionar Ano</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6} lg={4}>
                                    <Form.Group>
                                        <Form.Label>Ano Fiscal</Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={formData.selectedYear}
                                            onChange={(e) => handleYearChange(e.target.value)}
                                            className="mb-3"
                                        >
                                            <option value="">Selecione um ano</option>
                                            {years.map(year => (
                                                <option key={year.ano_fiscal_id} value={year.ano_fiscal_id}>
                                                    {year.ano} - {year.status}
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Entity Selection */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Selecionar Entidades</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6} lg={4}>
                                    <Form.Group>
                                        <Form.Label>Entidades</Form.Label>
                                        <InputGroup className="mb-2">
                                            <Form.Control
                                                type="text"
                                                placeholder="Filtrar entidades..."
                                                value={entityFilter}
                                                onChange={(e) => setEntityFilter(e.target.value)}
                                            />
                                            {entityFilter && (
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => setEntityFilter('')}
                                                    className="d-flex align-items-center"
                                                >
                                                    ×
                                                </Button>
                                            )}
                                        </InputGroup>
                                        <Form.Control
                                            as="select"
                                            multiple
                                            value={formData.selectedEntities}
                                            onChange={(e) => {
                                                // Prevent default behavior to handle selection manually
                                                e.preventDefault();
                                            }}
                                            className="mb-3"
                                            style={{ minHeight: '200px' }}
                                        >
                                            {filteredEntities.map(entity => (
                                                <option
                                                    key={entity.entidade_id}
                                                    value={entity.entidade_id}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleEntitySelection(entity.entidade_id, !formData.selectedEntities.includes(entity.entidade_id));
                                                    }}
                                                >
                                                    {entity.nome} - {entity.status}
                                                </option>
                                            ))}
                                        </Form.Control>
                                        <div className="d-flex gap-2 mb-3">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={handleSelectAllEntities}
                                            >
                                                Selecionar Todas
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={handleClearAllEntities}
                                            >
                                                Limpar Seleção
                                            </Button>
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Selected Entities Display */}
                            {formData.selectedEntities.length > 0 && (
                                <div className="mt-3">
                                    <h6>Entidades Selecionadas:</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                        {formData.selectedEntities.map(entityId => {
                                            const entity = entities.find(e => e.entidade_id === entityId);
                                            return entity ? (
                                                <Badge
                                                    key={entityId}
                                                    bg="success"
                                                    className="p-2 d-flex align-items-center gap-1"
                                                >
                                                    {entity.nome}
                                                    <Button
                                                        variant="outline-light"
                                                        size="sm"
                                                        className="ms-2 p-0 border-0"
                                                        onClick={() => handleEntitySelection(entityId, false)}
                                                    >
                                                        ×
                                                    </Button>
                                                </Badge>
                                            ) : null;
                                        })}
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Area Selection */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Selecionar Áreas</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6} lg={4}>
                                    <Form.Group>
                                        <Form.Label>Áreas</Form.Label>
                                        <InputGroup className="mb-2">
                                            <Form.Control
                                                type="text"
                                                placeholder="Filtrar áreas..."
                                                value={areaFilter}
                                                onChange={(e) => setAreaFilter(e.target.value)}
                                            />
                                            {areaFilter && (
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => setAreaFilter('')}
                                                    className="d-flex align-items-center"
                                                >
                                                    ×
                                                </Button>
                                            )}
                                        </InputGroup>
                                        <Form.Control
                                            as="select"
                                            multiple
                                            value={formData.selectedAreas}
                                            onChange={(e) => {
                                                // Prevent default behavior to handle selection manually
                                                e.preventDefault();
                                            }}
                                            className="mb-3"
                                            style={{ minHeight: '200px' }}
                                        >
                                            {filteredAreas.map(area => (
                                                <option
                                                    key={area.area_id}
                                                    value={area.area_id || ''}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleAreaSelection(area.area_id || '', !formData.selectedAreas.includes(area.area_id || ''));
                                                    }}
                                                >
                                                    {area.nome} - {area.status}
                                                </option>
                                            ))}
                                        </Form.Control>
                                        <div className="d-flex gap-2 mb-3">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={handleSelectAllAreas}
                                            >
                                                Selecionar Todas
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={handleClearAllAreas}
                                            >
                                                Limpar Seleção
                                            </Button>
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Selected Areas Display */}
                            {formData.selectedAreas.length > 0 && (
                                <div className="mt-3">
                                    <h6>Áreas Selecionadas:</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                        {formData.selectedAreas.map(areaId => {
                                            const area = areas.find(a => a.area_id === areaId);
                                            return area ? (
                                                <Badge
                                                    key={areaId}
                                                    bg="info"
                                                    className="p-2 d-flex align-items-center gap-1"
                                                >
                                                    {area.nome}
                                                    <Button
                                                        variant="outline-light"
                                                        size="sm"
                                                        className="ms-2 p-0 border-0"
                                                        onClick={() => handleAreaSelection(areaId, false)}
                                                    >
                                                        ×
                                                    </Button>
                                                </Badge>
                                            ) : null;
                                        })}
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Question Selection */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Selecionar Questões</h5>
                            {formData.selectedAreas.length > 0 && (
                                <small className="text-muted">
                                    Mostrando questões das {formData.selectedAreas.length} área(s) selecionada(s)
                                    ({filteredQuestions.length} questão(ões) disponível(is))
                                </small>
                            )}
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6} lg={4}>
                                    <Form.Group>
                                        <Form.Label>Questões</Form.Label>
                                        <InputGroup className="mb-2">
                                            <Form.Control
                                                type="text"
                                                placeholder="Filtrar questões..."
                                                value={questionFilter}
                                                onChange={(e) => setQuestionFilter(e.target.value)}
                                                disabled={formData.selectedAreas.length === 0}
                                            />
                                            {questionFilter && (
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => setQuestionFilter('')}
                                                    className="d-flex align-items-center"
                                                    disabled={formData.selectedAreas.length === 0}
                                                >
                                                    ×
                                                </Button>
                                            )}
                                        </InputGroup>
                                        <Form.Control
                                            as="select"
                                            multiple
                                            value={formData.selectedQuestions}
                                            onChange={(e) => {
                                                // Prevent default behavior to handle selection manually
                                                e.preventDefault();
                                            }}
                                            className="mb-3"
                                            disabled={formData.selectedAreas.length === 0}
                                            style={{ minHeight: '200px' }}
                                        >
                                            {filteredQuestions.length === 0 ? (
                                                <option disabled>Nenhuma questão disponível para as áreas selecionadas</option>
                                            ) : (
                                                filteredQuestions.map(question => (
                                                    <option
                                                        key={question.questaoBaseId}
                                                        value={question.questaoBaseId || ''}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleQuestionSelection(question.questaoBaseId || '', !formData.selectedQuestions.includes(question.questaoBaseId || ''));
                                                        }}
                                                    >
                                                        {question.titulo} - {question.tipoBase}
                                                    </option>
                                                ))
                                            )}
                                        </Form.Control>
                                        <div className="d-flex gap-2 mb-3">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={handleSelectAllQuestions}
                                                disabled={filteredQuestions.length === 0}
                                            >
                                                Selecionar Todas ({filteredQuestions.length})
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={handleClearAllQuestions}
                                            >
                                                Limpar Seleção
                                            </Button>
                                        </div>
                                        {formData.selectedAreas.length === 0 && (
                                            <small className="text-muted">
                                                ⚠️ Selecione pelo menos uma área primeiro para ver as questões disponíveis
                                            </small>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Selected Questions Display */}
                            {formData.selectedQuestions.length > 0 && (
                                <div className="mt-3">
                                    <h6>Questões Selecionadas:</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                        {formData.selectedQuestions.map(questionId => {
                                            const question = questions.find(q => q.questaoBaseId === questionId);
                                            return question ? (
                                                <Badge
                                                    key={questionId}
                                                    bg="primary"
                                                    className="p-2 d-flex align-items-center gap-1"
                                                >
                                                    {question.titulo}
                                                    <Button
                                                        variant="outline-light"
                                                        size="sm"
                                                        className="ms-2 p-0 border-0"
                                                        onClick={() => handleQuestionSelection(questionId, false)}
                                                    >
                                                        ×
                                                    </Button>
                                                </Badge>
                                            ) : null;
                                        })}
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Item Selection */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Selecionar Itens</h5>
                            {formData.selectedQuestions.length > 0 && (
                                <small className="text-muted">
                                    Mostrando itens das {formData.selectedQuestions.length} questão(ões) selecionada(s)
                                    ({filteredItems.length} item(ns) disponível(is))
                                </small>
                            )}
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6} lg={4}>
                                    <Form.Group>
                                        <Form.Label>Itens</Form.Label>
                                        <InputGroup className="mb-2">
                                            <Form.Control
                                                type="text"
                                                placeholder="Filtrar itens..."
                                                value={itemFilter}
                                                onChange={(e) => setItemFilter(e.target.value)}
                                                disabled={formData.selectedQuestions.length === 0}
                                            />
                                            {itemFilter && (
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => setItemFilter('')}
                                                    className="d-flex align-items-center"
                                                    disabled={formData.selectedQuestions.length === 0}
                                                >
                                                    ×
                                                </Button>
                                            )}
                                        </InputGroup>
                                        <Form.Control
                                            as="select"
                                            multiple
                                            value={formData.selectedItems}
                                            onChange={(e) => {
                                                // Prevent default behavior to handle selection manually
                                                e.preventDefault();
                                            }}
                                            className="mb-3"
                                            disabled={formData.selectedQuestions.length === 0}
                                            style={{ minHeight: '200px' }}
                                        >
                                            {filteredItems.length === 0 ? (
                                                <option disabled>Nenhum item disponível para as questões selecionadas</option>
                                            ) : (
                                                filteredItems.map(item => (
                                                    <option
                                                        key={item.itemBaseId}
                                                        value={item.itemBaseId || ''}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleItemSelection(item.itemBaseId || '', !formData.selectedItems.includes(item.itemBaseId || ''));
                                                        }}
                                                    >
                                                        {item.titulo} - {item.tipoBase}
                                                    </option>
                                                ))
                                            )}
                                        </Form.Control>
                                        <div className="d-flex gap-2 mb-3">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={handleSelectAllItems}
                                                disabled={filteredItems.length === 0}
                                            >
                                                Selecionar Todos ({filteredItems.length})
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={handleClearAllItems}
                                            >
                                                Limpar Seleção
                                            </Button>
                                        </div>
                                        {formData.selectedQuestions.length === 0 && (
                                            <small className="text-muted">
                                                ⚠️ Selecione pelo menos uma questão primeiro para ver os itens disponíveis
                                            </small>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Selected Items Display */}
                            {formData.selectedItems.length > 0 && (
                                <div className="mt-3">
                                    <h6>Itens Selecionados:</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                        {formData.selectedItems.map(itemId => {
                                            const item = items.find(i => i.itemBaseId === itemId);
                                            return item ? (
                                                <Badge
                                                    key={itemId}
                                                    bg="info"
                                                    className="p-2 d-flex align-items-center gap-1"
                                                >
                                                    {item.titulo}
                                                    <Button
                                                        variant="outline-light"
                                                        size="sm"
                                                        className="ms-2 p-0 border-0"
                                                        onClick={() => handleItemSelection(itemId, false)}
                                                    >
                                                        ×
                                                    </Button>
                                                </Badge>
                                            ) : null;
                                        })}
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Action Selection */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Selecionar Ações</h5>
                            {formData.selectedItems.length > 0 && (
                                <small className="text-muted">
                                    Mostrando ações dos {formData.selectedItems.length} item(ns) selecionado(s)
                                    ({filteredActions.length} ação(ões) disponível(is))
                                </small>
                            )}
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6} lg={4}>
                                    <Form.Group>
                                        <Form.Label>Ações</Form.Label>
                                        <InputGroup className="mb-2">
                                            <Form.Control
                                                type="text"
                                                placeholder="Filtrar ações..."
                                                value={actionFilter}
                                                onChange={(e) => setActionFilter(e.target.value)}
                                                disabled={formData.selectedItems.length === 0}
                                            />
                                            {actionFilter && (
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => setActionFilter('')}
                                                    className="d-flex align-items-center"
                                                    disabled={formData.selectedItems.length === 0}
                                                >
                                                    ×
                                                </Button>
                                            )}
                                        </InputGroup>
                                        <Form.Control
                                            as="select"
                                            multiple
                                            value={formData.selectedActions}
                                            onChange={(e) => {
                                                // Prevent default behavior to handle selection manually
                                                e.preventDefault();
                                            }}
                                            className="mb-3"
                                            disabled={formData.selectedItems.length === 0}
                                            style={{ minHeight: '200px' }}
                                        >
                                            {filteredActions.length === 0 ? (
                                                <option disabled>Nenhuma ação disponível para os itens selecionados</option>
                                            ) : (
                                                filteredActions.map(action => (
                                                    <option
                                                        key={action.acaoBaseId}
                                                        value={action.acaoBaseId || ''}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleActionSelection(action.acaoBaseId || '', !formData.selectedActions.includes(action.acaoBaseId || ''));
                                                        }}
                                                    >
                                                        {action.titulo} - {action.tipoBase}
                                                    </option>
                                                ))
                                            )}
                                        </Form.Control>
                                        <div className="d-flex gap-2 mb-3">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={handleSelectAllActions}
                                                disabled={filteredActions.length === 0}
                                            >
                                                Selecionar Todas ({filteredActions.length})
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={handleClearAllActions}
                                            >
                                                Limpar Seleção
                                            </Button>
                                        </div>
                                        {formData.selectedItems.length === 0 && (
                                            <small className="text-muted">
                                                ⚠️ Selecione pelo menos um item primeiro para ver as ações disponíveis
                                            </small>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Selected Actions Display */}
                            {formData.selectedActions.length > 0 && (
                                <div className="mt-3">
                                    <h6>
                                        Ações Selecionadas:
                                        <span className="text-muted ms-2">
                                            {formData.selectedActions.length} total
                                            {formData.selectedActions.length > 50 && (
                                                <span className="text-info ms-1">(Mostrando primeiras 50)</span>
                                            )}
                                        </span>
                                    </h6>
                                    <div className="d-flex flex-wrap gap-2">
                                        {formData.selectedActions.slice(0, 50).map(actionId => {
                                            const action = actions.find(a => a.acaoBaseId === actionId);
                                            return action ? (
                                                <Badge
                                                    key={actionId}
                                                    bg="warning"
                                                    className="p-2 d-flex align-items-center gap-1"
                                                >
                                                    {action.titulo}
                                                    <Button
                                                        variant="outline-light"
                                                        size="sm"
                                                        className="ms-2 p-0 border-0"
                                                        onClick={() => handleActionSelection(actionId, false)}
                                                    >
                                                        ×
                                                    </Button>
                                                </Badge>
                                            ) : null;
                                        })}
                                        {formData.selectedActions.length > 50 && (
                                            <Badge bg="secondary" className="p-2">
                                                +{formData.selectedActions.length - 50} ações adicionais
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Summary and Publish Button */}
            <Row>
                <Col>
                    <Card className="bg-light">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="mb-1">Resumo da Publicação:</h6>
                                    <div className="text-muted">
                                        <small>
                                            <strong>Ano:</strong> {formData.selectedYear ? getSelectedYearName() : 'Não selecionado'} |
                                            <strong> Entidades:</strong> {formData.selectedEntities.length} selecionada(s) |
                                            <strong> Áreas:</strong> {formData.selectedAreas.length} selecionada(s) |
                                            <strong> Questões:</strong> {formData.selectedQuestions.length} selecionada(s) |
                                            <strong> Itens:</strong> {formData.selectedItems.length} selecionado(s) |
                                            <strong> Ações:</strong> {formData.selectedActions.length} selecionada(s)
                                        </small>
                                    </div>
                                </div>
                                <Button
                                    variant="primary"
                                    onClick={handlePublishClick}
                                    disabled={
                                        !formData.selectedYear ||
                                        formData.selectedEntities.length === 0 ||
                                        formData.selectedAreas.length === 0 ||
                                        formData.selectedQuestions.length === 0 ||
                                        formData.selectedItems.length === 0 ||
                                        formData.selectedActions.length === 0
                                    }
                                    size="lg"
                                >
                                    Publicar Questões, Itens e Ações
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Confirmation Modal */}
            <Modal show={showPublishModal} onHide={handlePublishModalClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar Publicação</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning">
                        <strong>Atenção:</strong> Esta ação irá publicar as questões, itens e ações configurados para o ano e entidades selecionados.
                    </Alert>

                    <div className="confirmation-details">
                        <h6>Detalhes da Publicação:</h6>
                        <ul className="list-unstyled">
                            <li><strong>Ano:</strong> {getSelectedYearName()}</li>
                            <li><strong>Entidades:</strong> {formData.selectedEntities.length} selecionada(s)</li>
                            <li><strong>Áreas:</strong> {formData.selectedAreas.length} selecionada(s)</li>
                            <li><strong>Questões:</strong> {formData.selectedQuestions.length} selecionada(s)</li>
                            <li><strong>Itens:</strong> {formData.selectedItems.length} selecionado(s)</li>
                            <li><strong>Ações:</strong> {formData.selectedActions.length} selecionada(s)</li>
                        </ul>
                    </div>

                    <Alert variant="info">
                        <small>
                            As questões, itens e ações publicados estarão disponíveis para os usuários das entidades selecionadas.
                            Esta ação não pode ser desfeita.
                        </small>
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handlePublishModalClose} disabled={isPublishing}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmitPublish}
                        disabled={isPublishing}
                    >
                        {isPublishing ? 'Publicando...' : 'Confirmar Publicação'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PublishAdminPage;
